/**
 * src/components/feature/viewer/ViewerController.js
 * 
 * Premium Floating Action Button (FAB) Controller
 * Handles:
 * 1. Auto-Play (Horizontal Slide vs Vertical Scroll)
 * 2. Media Controls (BGM, TTS)
 * 3. Settings Shortcut
 * 4. Scroll To Top
 * 5. Translation (New)
 */
import { createIcons, Plus, X, ArrowUp, Music, Pause, Play, Headphones, VolumeX, Settings, GalleryHorizontal, Sparkles, Globe } from 'lucide';
import { locale } from '../../../services/LocaleService.js';

export class ViewerController {
    /**
     * @param {HTMLElement} container - Parent container.
     * @param {Object} props
     * @param {string} props.viewerType - 'vertical', 'horizontal', 'novel', 'davinci'.
     * @param {string} props.bgmUrl - URL for background music.
     * @param {string} props.content - Text content for TTS (Novel/DaVinci only).
     * @param {Function} props.onScrollTop - Callback to scroll to top.
     * @param {Function} props.onNextPage - Callback for Auto-Turn (Horizontal).
     * @param {Function} props.onTranslate - Callback for content translation.
     */
    constructor(container, { viewerType, bgmUrl, content, onScrollTop, onNextPage, onTranslate }) {
        this.container = container;
        this.viewerType = viewerType;
        this.bgmUrl = bgmUrl;
        this.content = content;
        this.onScrollTop = onScrollTop;
        this.onNextPage = onNextPage;
        this.onTranslate = onTranslate; // New Callback

        // State
        this.isOpen = false;
        this.isPlayingBGM = false;
        this.isPlayingTTS = false;
        this.isAutoPlaying = false;
        this.isTranslated = false;

        // Settings State
        this.bgmVolume = 0.5;
        this.autoSpeed = 1; // 1x default

        // Resources
        this.audio = null;
        this.synth = window.speechSynthesis;
        this.utterance = null;

        // Timers
        this.autoPlayTimer = null; // For Horizontal Slide (setInterval)
        this.autoScrollFrame = null; // For Vertical Scroll (RAF)

        // Global Cleanup Hook
        if (window.activeViewerController) {
            try { window.activeViewerController.destroy(); } catch (e) { }
        }
        window.activeViewerController = this;

        this.init();

        // [FIX] Warm up TTS Engine for Android
        if (this.synth && this.synth.getVoices) {
            this.synth.getVoices();
        }
    }

    init() {
        this.setupAudio();
        this.render();
        // Ensure styling and icons are applied
        setTimeout(() => {
            try {
                createIcons({ icons: { Plus, X, ArrowUp, Music, Pause, Play, Headphones, VolumeX, Settings, GalleryHorizontal, Sparkles, Globe } });
            } catch (e) {
                console.warn("[ViewerController] Icon render warning:", e);
            }
        }, 50);
        this.refreshIcons();
    }

    setupAudio() {
        // [Cleaned] Debug logs removed per user request
        const convertToSunoMp3 = (url) => {
            if (!url) return '';
            url = url.trim();
            if (url.endsWith('.mp3') || url.endsWith('.wav')) return url;
            const sunoRegex = /\/song\/([0-9a-fA-F-]{36})/;
            const match = url.match(sunoRegex);
            return (match && match[1]) ? `https://cdn1.suno.ai/${match[1]}.mp3` : url;
        };

        if (this.bgmUrl) {
            const safeUrl = convertToSunoMp3(this.bgmUrl);
            // [FIX] Removed strict regex validation (.mp3/.wav) to allow complex URLs (e.g. signed URLs, CDN params)
            // if (!safeUrl.match(/\.(mp3|wav)$/i)) { ... } 

            this.bgmUrl = safeUrl;
            this.audio = new Audio(safeUrl);
            this.audio.loop = true;
            this.audio.volume = this.bgmVolume;
        }
    }

    render() {
        const wrapper = document.createElement('div');
        wrapper.id = "viewer-controller-wrapper";
        // Fixed overlay for the entire controller system
        // [FIX] wrapper is PERMANENTLY pointer-events-none to prevent blocking touch on screen
        // [FIX] Z-Index Hierarchy: Content(0) < Header(100) < FAB(500) < Settings(1000) < Modal(2000)
        wrapper.className = "fixed inset-0 z-[500] pointer-events-none";

        // Determine availability for "Always Render" strategy
        const hasBgm = !!this.bgmUrl;
        // Basic check for text content (Navi/Webtoon might not have text)
        const hasText = !!this.content && this.content.length > 0;
        // Translation available?
        // [FIX] Allow translation even if no text content (for OCR Image Translation)
        const canTranslate = !!this.onTranslate;

        // Top button meaningful? (Always show for consistency, maybe redundant for horizontal but harmless)
        const isHorizontal = this.viewerType === 'horizontal';

        // Disabled State Styles (Dimmed & Cursor ban)
        // [FIX] Added 'btn-disabled' for logic scraping
        const disabledClass = "opacity-40 grayscale cursor-not-allowed btn-disabled";

        // [FIX] Default interaction state for ALL hidden buttons is NONE (Ghost Click Prevention)
        const defaultInteractionMode = "pointer-events-none";

        wrapper.innerHTML = `
            <!-- FAB Container: Fixed Bottom Right Safe Area -->
            <!-- [FIX] Positioned at bottom-[20vh] to safely clear footer/settings on mobile -->
            <div id="viewer-controller" class="fixed bottom-[20vh] right-4 md:right-8 flex flex-col items-end gap-3 font-sans pointer-events-none transition-all duration-300 z-[9999]">
                
                <!-- Settings Panel -->
                <!-- [FIX] pointer-events-auto added so it can be clicked -->
                <div id="settings-panel" class="hidden mb-2 p-4 bg-zinc-900/95 text-white rounded-2xl shadow-2xl border border-zinc-700 w-64 backdrop-blur-md transition-all duration-300 origin-bottom-right transform scale-95 opacity-0 pointer-events-auto">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-sm font-bold text-zinc-300">${locale.t('viewer.settings_title')}</h3>
                        <button id="close-settings" class="text-zinc-400 hover:text-white"><i data-lucide="x" class="w-4 h-4"></i></button>
                    </div>
                    
                    ${hasBgm ? `
                    <div class="mb-4">
                        <label class="text-xs text-zinc-400 mb-1 block">${locale.t('viewer.bgm_volume')}</label>
                        <input type="range" id="vol-slider" min="0" max="1" step="0.1" value="${this.bgmVolume}" class="w-full accent-violet-500 h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer">
                    </div>
                    ` : `
                    <div class="mb-4 opacity-50">
                        <label class="text-xs text-zinc-400 mb-1 block">${locale.t('viewer.bgm_off')}</label>
                        <div class="w-full h-1 bg-zinc-800 rounded-lg"></div>
                    </div>
                    `}
                    
                    <div class="mb-2">
                        <label class="text-xs text-zinc-400 mb-1 block">${locale.t('viewer.auto_speed')}</label>
                        <div class="flex gap-2">
                            <button class="flex-1 bg-zinc-800 hover:bg-zinc-700 text-xs py-1.5 rounded transition-colors speed-btn active" data-speed="1">x1</button>
                            <button class="flex-1 bg-zinc-800 hover:bg-zinc-700 text-xs py-1.5 rounded transition-colors speed-btn" data-speed="2">x2</button>
                            <button class="flex-1 bg-zinc-800 hover:bg-zinc-700 text-xs py-1.5 rounded transition-colors speed-btn" data-speed="3">x3</button>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons (Speed Dial) -->
                <!-- [FIX] Container is pointer-events-none. Initially. -->
                <div id="fab-actions" class="flex flex-col items-end gap-3 transition-all duration-300 translate-y-4 opacity-0 pointer-events-none">
                    
                    <!-- 1. Settings (Always Active) -->
                    <!-- [FIX] Explicitly pointer-events-none to prevent ghost clicks when closed -->
                    <button type="button" id="btn-settings" class="flex items-center justify-end gap-3 group ${defaultInteractionMode}">
                        <span class="bg-zinc-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">${locale.t('viewer.settings_btn')}</span>
                        <div class="w-11 h-11 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-full shadow-lg border border-zinc-100 dark:border-zinc-700 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors">
                            <i data-lucide="settings" class="w-5 h-5"></i>
                        </div>
                    </button>

                    <!-- 2. Translate (New) -->
                    <button type="button" id="btn-translate" class="flex items-center justify-end gap-3 group ${defaultInteractionMode} ${canTranslate ? '' : disabledClass}">
                        <span class="bg-zinc-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">${locale.t('viewer.translate_btn')}</span>
                        <div class="bg-btn w-11 h-11 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-full shadow-lg border border-zinc-100 dark:border-zinc-700 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors">
                            <i data-lucide="globe" class="w-5 h-5 icon-default"></i>
                            <i data-lucide="sparkles" class="w-5 h-5 icon-active text-violet-500 hidden"></i>
                        </div>
                    </button>

                    <!-- 3. TTS (Dependent on Text) -->
                    <button type="button" id="btn-tts" class="flex items-center justify-end gap-3 group ${defaultInteractionMode} ${hasText ? '' : disabledClass}">
                        <span class="bg-zinc-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">${locale.t('viewer.tts_btn')}</span>
                        <div class="bg-btn w-11 h-11 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-full shadow-lg border border-zinc-100 dark:border-zinc-700 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors">
                            <i data-lucide="headphones" class="w-5 h-5 icon-default"></i>
                            <i data-lucide="volume-x" class="w-5 h-5 icon-active hidden"></i>
                        </div>
                    </button>

                    <!-- 4. BGM (Dependent on URL) -->
                    <button type="button" id="btn-bgm" class="flex items-center justify-end gap-3 group ${defaultInteractionMode} ${hasBgm ? '' : disabledClass}">
                        <span class="bg-zinc-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">${locale.t('viewer.bgm_btn')}</span>
                        <div class="bg-btn w-11 h-11 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-full shadow-lg border border-zinc-100 dark:border-zinc-700 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors">
                            <i data-lucide="music" class="w-5 h-5 icon-default"></i>
                            <i data-lucide="pause" class="w-5 h-5 icon-active hidden"></i>
                        </div>
                    </button>

                    <!-- 5. Top (Always shown) -->
                    <button type="button" id="btn-top" class="flex items-center justify-end gap-3 group ${defaultInteractionMode}">
                        <span class="bg-zinc-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">${locale.t('viewer.top_btn')}</span>
                        <div class="w-11 h-11 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-full shadow-lg border border-zinc-100 dark:border-zinc-700 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors">
                            <i data-lucide="arrow-up" class="w-5 h-5"></i>
                        </div>
                    </button>
                    
                    <!-- 6. Auto Play (Always Show) -->
                    <button type="button" id="btn-autoplay" class="flex items-center justify-end gap-3 group ${defaultInteractionMode}">
                        <span class="bg-zinc-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">${isHorizontal ? locale.t('viewer.auto_play') : locale.t('viewer.auto_scroll')}</span>
                        <div class="bg-btn w-11 h-11 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-full shadow-lg border border-zinc-100 dark:border-zinc-700 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors">
                            <i data-lucide="${isHorizontal ? 'gallery-horizontal' : 'play'}" class="w-5 h-5 icon-default"></i>
                            <i data-lucide="pause" class="w-5 h-5 icon-active hidden"></i>
                        </div>
                    </button>

                </div>

                <!-- Main Trigger -->
                <!-- [FIX] Explicitly pointer-events-auto -->
                <button id="fab-main" class="w-14 h-14 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-2xl flex items-center justify-center transition-transform duration-300 active:scale-95 pointer-events-auto ring-4 ring-zinc-900">
                    <i data-lucide="sparkles" class="w-6 h-6 transition-transform duration-300" id="fab-plus"></i>
                    <i data-lucide="x" class="w-6 h-6 absolute transition-opacity duration-300 opacity-0" id="fab-close"></i>
                </button>
            </div>
        `;

        document.body.appendChild(wrapper);
        this.wrapper = wrapper;

        this.bindEvents();
    }

    bindEvents() {
        if (!this.wrapper) return;

        // Toggle Main FAB
        const fab = this.wrapper.querySelector('#fab-main');
        const actions = this.wrapper.querySelector('#fab-actions');
        const iconPlus = this.wrapper.querySelector('#fab-plus');
        const iconClose = this.wrapper.querySelector('#fab-close');

        if (fab) fab.onclick = () => {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                // [OPEN]
                // 1. Show container
                actions.classList.remove('translate-y-4', 'opacity-0');

                // 2. Enable Pointer Events ONLY for VALID buttons (Double Lock)
                // This ensures disabled buttons or the container itself don't capture stray clicks
                const validButtons = actions.querySelectorAll('button:not(.btn-disabled)');
                validButtons.forEach(btn => {
                    btn.classList.remove('pointer-events-none');
                    btn.classList.add('pointer-events-auto');
                });

                iconPlus.classList.add('rotate-45', 'opacity-0');
                iconClose.classList.remove('opacity-0');
                iconClose.classList.add('rotate-0');
            } else {
                // [CLOSE]
                // 1. Hide container
                actions.classList.add('translate-y-4', 'opacity-0');

                // 2. DISABLE Pointer Events for ALL buttons force-ably (Double Lock)
                const allButtons = actions.querySelectorAll('button');
                allButtons.forEach(btn => {
                    btn.classList.remove('pointer-events-auto');
                    btn.classList.add('pointer-events-none');
                });

                this.closeSettings();
                iconPlus.classList.remove('rotate-45', 'opacity-0');
                iconClose.classList.add('opacity-0');
                iconClose.classList.remove('rotate-0');
            }
        };

        // Scroll Top
        const btnTop = this.wrapper.querySelector('#btn-top');
        if (btnTop) btnTop.onclick = () => {
            if (this.onScrollTop) {
                this.onScrollTop();
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };

        // Settings Toggle
        const btnSettings = this.wrapper.querySelector('#btn-settings');
        if (btnSettings) btnSettings.onclick = () => {
            const panel = this.wrapper.querySelector('#settings-panel');
            if (panel.classList.contains('hidden')) {
                panel.classList.remove('hidden');
                setTimeout(() => panel.classList.remove('opacity-0', 'scale-95'), 10);
            } else {
                this.closeSettings();
            }
        };

        const closeSettingsBtn = this.wrapper.querySelector('#close-settings');
        if (closeSettingsBtn) closeSettingsBtn.onclick = () => this.closeSettings();

        // Volume Slider
        const volSlider = this.wrapper.querySelector('#vol-slider');
        if (volSlider) volSlider.oninput = (e) => {
            this.bgmVolume = parseFloat(e.target.value);
            if (this.audio) this.audio.volume = this.bgmVolume;
        };

        // Speed Buttons
        const speedBtns = this.wrapper.querySelectorAll('.speed-btn');
        speedBtns.forEach(btn => {
            btn.onclick = () => {
                speedBtns.forEach(b => b.classList.remove('active', 'bg-indigo-600', 'text-white'));
                btn.classList.add('active', 'bg-indigo-600', 'text-white');
                this.autoSpeed = parseInt(btn.dataset.speed);

                // If currently playing, restart with new speed
                if (this.isAutoPlaying) {
                    this.stopAutoPlay();
                    this.startAutoPlay();
                }
            };
        });

        // BGM Toggle
        const btnBgm = this.wrapper.querySelector('#btn-bgm');
        if (btnBgm) btnBgm.onclick = () => this.toggleBGM(btnBgm);

        // TTS Toggle
        const btnTts = this.wrapper.querySelector('#btn-tts');
        if (btnTts) btnTts.onclick = () => this.toggleTTS(btnTts);

        // AutoPlay Toggle
        const btnAuto = this.wrapper.querySelector('#btn-autoplay');
        if (btnAuto) btnAuto.onclick = () => this.toggleAutoPlay(btnAuto);

        // Translate Toggle
        const btnTranslate = this.wrapper.querySelector('#btn-translate');
        if (btnTranslate) btnTranslate.onclick = () => this.toggleTranslate(btnTranslate);
    }

    closeSettings() {
        const panel = this.wrapper.querySelector('#settings-panel');
        if (panel) {
            panel.classList.add('opacity-0', 'scale-95');
            setTimeout(() => panel.classList.add('hidden'), 300);
        }
    }

    refreshIcons() {
        if (window.lucide) {
            window.lucide.createIcons();
        }
    }

    // --- Logic ---

    toggleBGM(btn) {
        if (!this.audio) return;

        this.isPlayingBGM = !this.isPlayingBGM;
        if (this.isPlayingBGM) {
            this.audio.play().catch(e => console.error("Audio Play Error:", e));
            this.toggleBtnState(btn, true);
        } else {
            this.audio.pause();
            this.toggleBtnState(btn, false);
        }
    }

    // [FIX] Android TTS Chunking Strategy
    // Android SpeechSynthesis fails with long text (>4000 chars) or single large utterances.
    // We must split content into sentences and play them sequentially.

    toggleTTS(btn) {
        if (!this.synth || !this.content) return;

        this.isPlayingTTS = !this.isPlayingTTS;
        if (this.isPlayingTTS) {
            // [UX] Warn user that TTS plays original text if translated
            if (this.isTranslated) {
                window.Modal.toast(locale.t('viewer.tts_warn'));
            }

            // [FIX] Always cancel previous before starting new (Android safety)
            this.synth.cancel();

            // Initialize Chunks
            this.ttsChunks = this.splitContentToChunks(this.content);
            this.currentChunkIndex = 0;

            this.speakNextChunk(btn);
            this.toggleBtnState(btn, true);
        } else {
            this.synth.cancel(); // Pause is buggy on Android, Cancel is safer
            this.toggleBtnState(btn, false);
            this.ttsChunks = [];
            this.currentChunkIndex = 0;
        }
    }

    splitContentToChunks(text) {
        // Split by sentence ending punctuation (. ? !)
        // Keep the punctuation with the sentence
        // Also limit max length to ~200 chars to ensure stability
        if (!text) return [];

        // 1. Simple split by punctuation
        const rawSentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];

        // 2. Refine chunk size (merge short, split long)
        const chunks = [];
        let currentChunk = "";

        rawSentences.forEach(sentence => {
            sentence = sentence.trim();
            if (!sentence) return;

            if ((currentChunk + sentence).length < 200) {
                currentChunk += (currentChunk ? " " : "") + sentence;
            } else {
                if (currentChunk) chunks.push(currentChunk);
                currentChunk = sentence;
            }
        });
        if (currentChunk) chunks.push(currentChunk);

        return chunks;
    }

    speakNextChunk(btn) {
        if (!this.isPlayingTTS) return;
        if (this.currentChunkIndex >= this.ttsChunks.length) {
            // Done
            this.isPlayingTTS = false;
            this.toggleBtnState(btn, false);
            return;
        }

        const chunk = this.ttsChunks[this.currentChunkIndex];
        this.utterance = new SpeechSynthesisUtterance(chunk);
        this.utterance.lang = 'ko-KR';
        this.utterance.rate = 1.0; // Fixed rate for now

        // [FIX] Improve Voice Selection for Android
        const voices = this.synth.getVoices();
        const koVoice = voices.find(v => v.lang === 'ko-KR' || v.lang === 'ko_KR') || voices.find(v => v.lang.startsWith('ko'));
        if (koVoice) this.utterance.voice = koVoice;

        this.utterance.onend = () => {
            if (this.isPlayingTTS) {
                this.currentChunkIndex++;
                this.speakNextChunk(btn);
            }
        };

        this.utterance.onerror = (e) => {
            console.warn("TTS Error:", e);
            // Try next chunk even if error
            if (this.isPlayingTTS) {
                this.currentChunkIndex++;
                this.speakNextChunk(btn);
            }
        };

        this.synth.speak(this.utterance);
    }

    toggleTranslate(btn) {
        if (!this.onTranslate) return;

        this.isTranslated = !this.isTranslated; // Visual Toggle

        // Call Handler
        this.onTranslate(this.isTranslated);

        this.toggleBtnState(btn, this.isTranslated);
    }

    toggleAutoPlay(btn) {
        this.isAutoPlaying = !this.isAutoPlaying;
        if (this.isAutoPlaying) {
            this.startAutoPlay();
            this.toggleBtnState(btn, true);
            window.Modal.toast(this.viewerType === 'horizontal' ? locale.t('viewer.start_auto') : locale.t('viewer.start_auto'));
        } else {
            this.stopAutoPlay();
            this.toggleBtnState(btn, false);
            window.Modal.toast(locale.t('viewer.stop_auto'));
        }
    }

    startAutoPlay() {
        this.stopAutoPlay(); // Safety clear

        if (this.viewerType === 'horizontal' || this.viewerType.startsWith('comic')) {
            // Auto Slide Logic
            let interval = 5000;

            if (this.viewerType.startsWith('comic')) {
                // [FIX] Comic Mode (Dense Text) -> Slower Intervals
                // Speed 1: 8s, 2: 5s, 3: 3s
                interval = this.autoSpeed === 1 ? 8000 : this.autoSpeed === 2 ? 5000 : 3000;
            } else {
                // Webtoon Mode (Image Heavy) -> Faster
                // Speed 1: 5s, 2: 3s, 3: 2s
                interval = this.autoSpeed === 1 ? 5000 : this.autoSpeed === 2 ? 3000 : 2000;
            }

            this.autoPlayTimer = setInterval(() => {
                if (this.onNextPage) this.onNextPage();
            }, interval);

        } else {
            // Auto Scroll Logic (Vertical/Novel)
            // Speed 1: 1px, 2: 2px, 3: 3px per frame seems too fast.
            // Let's degrade: 1: 0.5px, 2: 1px, 3: 2px approx.
            const scrollAmount = this.autoSpeed === 1 ? 0.5 : this.autoSpeed === 2 ? 1.0 : 2.5;

            const scroll = () => {
                if (!this.isAutoPlaying) return;

                // Check if bottom reached
                if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50) {
                    this.toggleAutoPlay(this.wrapper.querySelector('#btn-autoplay')); // Stop
                    return;
                }

                window.scrollBy(0, scrollAmount);
                this.autoScrollFrame = requestAnimationFrame(scroll);
            };
            this.autoScrollFrame = requestAnimationFrame(scroll);
        }
    }

    stopAutoPlay() {
        if (this.autoPlayTimer) clearInterval(this.autoPlayTimer);
        if (this.autoScrollFrame) cancelAnimationFrame(this.autoScrollFrame);
        this.autoPlayTimer = null;
        this.autoScrollFrame = null;
    }

    forceStopAutoPlay() {
        if (this.isAutoPlaying) {
            this.toggleAutoPlay(this.wrapper.querySelector('#btn-autoplay'));
        }
    }

    toggleBtnState(btn, isActive) {
        const iconDefault = btn.querySelector('.icon-default');
        const iconActive = btn.querySelector('.icon-active');

        if (isActive) {
            iconDefault.classList.add('hidden');
            iconActive.classList.remove('hidden');
            btn.querySelector('.bg-btn').classList.add('bg-indigo-100', 'text-indigo-600', 'border-indigo-200');
        } else {
            iconDefault.classList.remove('hidden');
            iconActive.classList.add('hidden');
            btn.querySelector('.bg-btn').classList.remove('bg-indigo-100', 'text-indigo-600', 'border-indigo-200');
        }
    }

    /**
     * Updates content for TTS and re-evaluates button state
     * @param {string} newContent - Plain text for TTS
     */
    updateContent(newContent) {
        this.content = newContent;

        // Stop current TTS if playing
        if (this.isPlayingTTS) {
            this.toggleTTS(this.wrapper.querySelector('#btn-tts'));
        }

        // Update Button Visibility based on new content availability
        const btnTts = this.wrapper.querySelector('#btn-tts');
        const hasText = !!this.content && this.content.length > 0;
        const disabledClass = "opacity-40 grayscale cursor-not-allowed btn-disabled";

        if (btnTts) {
            // Reset classes
            btnTts.classList.remove('opacity-40', 'grayscale', 'cursor-not-allowed', 'btn-disabled', 'pointer-events-none');

            if (hasText) {
                // Enabled
                // (No special class needed, just remove disabled ones)
            } else {
                // Disabled
                btnTts.classList.add(...disabledClass.split(' '));
                // Ensure pointer-events-none if wrapper is closed (handled by toggle, but good to be safe)
            }
        }
    }

    destroy() {
        this.stopAutoPlay();
        if (this.synth) this.synth.cancel();
        if (this.audio) this.audio.pause();

        if (this.wrapper && this.wrapper.parentElement) {
            this.wrapper.remove();
        }
        if (window.activeViewerController === this) window.activeViewerController = null;
    }
}

